import React, {ReactNode} from "react";
import {Theme, Typography} from "@mui/material";
import {SxProps} from "@mui/system";
import {Colors} from "../colors";
import {LabelColors} from "../color_models";

const getBackgroundColor = (backgroundColor: string) => {
  if (backgroundColor === "green") {
    return Colors.green._900;
  }
  if (backgroundColor === "yellow") {
    return Colors.orange._800;
  }
  if (backgroundColor === "red") {
    return Colors.red._900;
  }
  if (backgroundColor === "primary") {
    return Colors.primary._800;
  }
  return backgroundColor;
};

const convertPxToRem = (pxSize: number) => {
  const rem = pxSize / 16;
  return `${rem}rem`;
};

export type ITextProps = {
  variant?:
    | "interDisplay400"
    | "interDisplay500"
    | "interDisplay600"
    | "interDisplay800"
    | "inter400"
    | "inter500"
    | "inter600"
    | "inter800"
    | "tiempos400"
    | "tiempos500"
    | "tiempos600"
    | "tiemposHead400"
    | "tiemposHead500"
    | "tiemposHead600";
  fontWeight?: "light" | "regular" | "medium" | "bold" | number; // 300 | 400 | 600 | 700
  size?: 10 | 11 | 12 | 13 | 14 | 16 | 18 | 20 | 22 | 24 | 26 | 28 | 32 | 36 | 40 | 50; // This is in px. in rem: 0.6875 | 0.75 | 0.8125 | 0.875 | 1 | 1.125 | 1.25 | 1.375 | 1.5 | 1.625 | 2 | 2.25 | 2.5 |3.125;
  color?: string;
  newLines?: boolean;
  sx?: SxProps<Theme>;
  children?: ReactNode;
  title?: string | undefined;
};

export interface ILabeledTextProps extends ITextProps {
  color?: string;
  backgroundColor?: LabelColors;
}

const getVariant = (size: number, fontWeight: string | number): ITextProps["variant"] | undefined => {
  if (size < 18) {
    if (["light", "regular", 500].includes(fontWeight)) {
      return "inter400";
    } else if (["medium", "bold"].includes(String(fontWeight))) {
      return "inter600";
    }
  } else {
    if (["light", "regular", 500].includes(fontWeight)) {
      return "interDisplay400";
    } else if (["medium", "bold"].includes(String(fontWeight))) {
      return "interDisplay600";
    }
  }
  return "inter400";
};

const getColor = (color?: string) => {
  if (color && color === "secondary") {
    return Colors.primary._800;
  }
  return color ?? Colors.grey._1000;
};

export const Text = ({
  fontWeight = "regular",
  size = 16,
  variant,
  color,
  newLines = false,
  sx,
  children,
  title,
}: ITextProps) => {
  const fontFamilyVariant = variant || getVariant(size, fontWeight);
  return (
    <Typography
      className={"Text"}
      component={"div"}
      // @ts-expect-error - renaming mui's variants
      variant={fontFamilyVariant}
      sx={{
        color: getColor(color),
        fontSize: convertPxToRem(size),
        alignItems: "center",
        ...(newLines && {whiteSpace: "pre-line"}),
        ...sx,
      }}
      title={title}
    >
      {children}
    </Typography>
  );
};

export const LabeledText = ({
  color,
  backgroundColor = "primary",
  variant,
  children,
  sx,
  ...props
}: ILabeledTextProps) => {
  return (
    <Text
      size={14}
      variant={variant}
      sx={{
        backgroundColor: getBackgroundColor(backgroundColor),
        color: color || Colors.bw.white,
        padding: "1px 10px",
        borderRadius: "5px",
        ...sx,
      }}
      {...props}
    >
      {children}
    </Text>
  );
};
